import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { faClose, faExpand } from "@fortawesome/pro-regular-svg-icons";
import { DialogTitle } from "@headlessui/react";
import { twJoin, twMerge } from "tailwind-merge";

import { useCaseBlockScrollTarget } from "src/cases/caseView/CaseBlockScrollContext";
import { Icon, IconButton } from "src/design-system/Icon";
import { Modal, useModal } from "src/design-system/Modal";
import { Skeleton } from "src/design-system/Skeleton";
import { Tooltip } from "src/design-system/Tooltip";

const ExpandedBlockContext = createContext(false);

export const useIsBlockExpanded = () => useContext(ExpandedBlockContext);

export type CaseBlockPosition = {
  column: number;
  row: number;
  width: number;
  height: number;
};

export type CaseBlockProps = {
  title: string;
  icon?: IconDefinition | null;
  rightSlot?: ReactNode;
  leftSlot?: ReactNode;
  children: ReactNode;
  position: CaseBlockPosition;
  noPaddings?: boolean;
  isLoading?: boolean;
  dataLoc?: string;
  blockKey: string;
  noScroll?: boolean;
  isExpandable?: boolean;
  expandedRightSlot?: ReactNode;
};

export const CaseBlock: React.FC<CaseBlockProps> = ({
  title,
  icon,
  rightSlot,
  leftSlot,
  children,
  position,
  noPaddings = false,
  isLoading = false,
  dataLoc = "case-block",
  blockKey,
  noScroll = false,
  isExpandable = false,
  expandedRightSlot,
}) => {
  const [isBlinking, triggerBlink] = useCaseBlockBlink();
  const {
    isOpen: isExpanded,
    openModal: openExpanded,
    closeModal: closeExpanded,
  } = useModal();

  const { ref: blockRef } = useCaseBlockScrollTarget<HTMLDivElement>(blockKey, {
    onScrollEnd: triggerBlink,
  });

  const gridPosition = {
    gridColumn: `${position.column} / span ${position.width}`,
    gridRow: `${position.row} / span ${position.height}`,
  };

  const expandButton = isExpandable && (
    <Tooltip delay={500} placement="top" title="Expand" asChild>
      <IconButton
        dataLoc="case-block-expand"
        icon={faExpand}
        size="xs"
        onClick={() => openExpanded()}
      />
    </Tooltip>
  );

  const headerActions = (expandButton || rightSlot) && (
    <div className="flex items-center self-stretch">
      {rightSlot}
      {expandButton && rightSlot && (
        <div className="-my-4 mr-4 ml-3 w-px self-stretch bg-gray-100" />
      )}
      {expandButton}
    </div>
  );

  return (
    <div
      ref={blockRef}
      className={twMerge(
        "flex scroll-mt-3 flex-col rounded-lg border border-gray-200 bg-white transition-all duration-300",
        isBlinking && "border-indigo-300 shadow-gray-200 shadow-lg",
      )}
      data-loc={isLoading ? `${dataLoc}-loading` : dataLoc}
      style={gridPosition}
    >
      <div
        className="flex items-center gap-2 border-gray-200 border-b p-4"
        data-loc={dataLoc ? `${dataLoc}-header` : "case-block-header"}
      >
        {icon && (
          <Icon
            variant="hover"
            dataLoc={dataLoc ? `${dataLoc}-icon` : "case-block-icon"}
            icon={icon}
            size="2xs"
          />
        )}
        <h3 className="flex-1 truncate font-inter-semibold-13px text-gray-900">
          {title}
        </h3>
        {leftSlot && (
          <div
            className="flex items-center"
            data-loc={dataLoc ? `${dataLoc}-left-slot` : "case-block-left-slot"}
          >
            {leftSlot}
          </div>
        )}
        {headerActions && (
          <div
            className="flex items-center"
            data-loc={
              dataLoc ? `${dataLoc}-right-slot` : "case-block-right-slot"
            }
          >
            {headerActions}
          </div>
        )}
      </div>
      <div
        className={twJoin(
          "flex flex-1 flex-col",
          noScroll ? "overflow-hidden" : "decideScrollbar overflow-auto",
          !noPaddings && "p-4",
        )}
        data-loc={dataLoc ? `${dataLoc}-content` : "case-block-content"}
      >
        {isLoading ? (
          <div className={twJoin("flex flex-col gap-4", noPaddings && "p-4")}>
            <Skeleton height="h-6" width="w-full" />
            <Skeleton height="h-6" width="w-full" />
            <Skeleton height="h-6" width="w-full" />
          </div>
        ) : (
          children
        )}
      </div>
      <Modal open={isExpanded} size="xl" onClose={closeExpanded}>
        {expandedRightSlot ? (
          <div className="flex items-stretch">
            <div className="flex flex-1 items-center gap-x-2 pt-4 pr-4 pb-3 pl-5 font-inter-semibold-16px text-gray-800">
              <DialogTitle className="flex-1 truncate">{title}</DialogTitle>
              {expandedRightSlot}
            </div>
            <div className="flex items-center border-gray-100 border-l pt-4 pr-4 pb-3 pl-4">
              <IconButton
                dataLoc="modal-close"
                icon={faClose}
                size="sm"
                onClick={() => closeExpanded()}
              />
            </div>
          </div>
        ) : (
          <Modal.Header>{title}</Modal.Header>
        )}
        <div className="h-screen min-h-0 flex-auto overflow-hidden border-gray-200 border-t">
          <ExpandedBlockContext value={true}>
            <div className="flex h-full flex-col">{children}</div>
          </ExpandedBlockContext>
        </div>
      </Modal>
    </div>
  );
};

const useCaseBlockBlink = () => {
  const [isBlinking, setIsBlinking] = useState<boolean>(false);
  const timeoutRef = useRef<number | null>(null);

  const triggerBlink = useCallback(() => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
    }

    setIsBlinking(true);

    timeoutRef.current = window.setTimeout(() => {
      setIsBlinking(false);
      timeoutRef.current = null;
    }, 600);
  }, []);

  useEffect(() => {
    return () => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return [isBlinking, triggerBlink] as const;
};
